import { priceDateKey } from '@/utils/circuits/prices/priceDateKey';
import { CircuitDayFormData } from '@/types/circuits/CircuitDaysFormData';
import { CircuitFormData } from '@/types/circuits/CircuitFormData';
import {
  CircuitDayPrices,
  getCircuitDayPricesObject,
  getCircuitDayTotalPrice,
} from '@/utils/circuits/prices/computeBenefitPrices';
import { getCircuitDaysWithMultiDayBenefitTracking } from '@/utils/circuits/prices/getCircuitDaysWithMultiDayBenefitTracking';
import {
  getLastPricedStartDate,
  mergeCircuitDaysPrices,
} from '@/utils/circuits/prices/circuitPricingWindow';
import {
  getDefaultPricingWindow,
  getMaxPricingDate,
  PricingWindow,
} from '@/utils/circuits/prices/pricingHorizon';
import { addDays, isAfter, min, startOfDay, startOfToday } from 'date-fns';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useConvertPrice } from './useCurrencyExchange';
import { SupportedLocale } from '@/types/supported-locale.type';

interface useCircuitPricesProps {
  days: CircuitFormData['circuitDays'];
  circuitMarginRate?: number;
  nbParticipants: number;
  nbChildren?: number;
  nbAdolescents?: number;
  childrenAges?: number[];
  targetCurrency?: string;
  creationDate?: Date;
  enabled?: boolean;
  targetDates?: Date[];
}

const NO_CHILDREN_AGES: number[] = [];

export const useCircuitPrices = ({
  days,
  circuitMarginRate = 0,
  nbParticipants,
  nbChildren = 0,
  nbAdolescents = 0,
  childrenAges = NO_CHILDREN_AGES,
  targetCurrency,
  creationDate,
  enabled = true,
  targetDates,
}: useCircuitPricesProps) => {
  const { convertPrice } = useConvertPrice(creationDate);

  const daysTrackingMultiDayBenefits = useMemo(
    () => (enabled ? getCircuitDaysWithMultiDayBenefitTracking(days) : []),
    [days, enabled],
  );

  // Fin de la fenêtre de dates déjà calculée. Elle part sur 11 mois et
  // s'étend quand l'utilisateur avance dans le calendrier (ensurePricedUntil) :
  // seules les dates manquantes sont alors calculées.
  const [pricingWindowEnd, setPricingWindowEnd] = useState(
    () => getDefaultPricingWindow().end,
  );

  const computeCircuitDaysPrices = useCallback(
    (pricingWindow: PricingWindow, withTargetDates: boolean) =>
      daysTrackingMultiDayBenefits.map((day, indexDay) =>
        getCircuitDayPricesObject({
          circuitDay: day,
          nbParticipants: nbParticipants,
          circuitMarginRate: circuitMarginRate
            ? Number(circuitMarginRate)
            : undefined,
          nbChildren,
          nbAdolescents,
          indexDay,
          childrenAges,
          targetDates: withTargetDates ? targetDates : undefined,
          pricingWindow,
        }),
      ),
    [
      childrenAges,
      circuitMarginRate,
      daysTrackingMultiDayBenefits,
      nbAdolescents,
      nbChildren,
      nbParticipants,
      targetDates,
    ],
  );

  const pricesCacheRef = useRef<{
    compute: typeof computeCircuitDaysPrices;
    windowEnd: Date;
    prices: CircuitDayPrices[];
  } | null>(null);

  const circuitDaysObject = useMemo(() => {
    if (!enabled) return [];

    const cache = pricesCacheRef.current;
    if (cache?.compute === computeCircuitDaysPrices) {
      if (!isAfter(pricingWindowEnd, cache.windowEnd)) return cache.prices;
      const prices = mergeCircuitDaysPrices(
        cache.prices,
        computeCircuitDaysPrices(
          { start: addDays(cache.windowEnd, 1), end: pricingWindowEnd },
          false,
        ),
      );
      pricesCacheRef.current = {
        ...cache,
        windowEnd: pricingWindowEnd,
        prices,
      };
      return prices;
    }

    // Les paramètres ont changé (participants, circuit…) : on recalcule toute
    // la fenêtre déjà ouverte.
    const prices = computeCircuitDaysPrices(
      { start: startOfToday(), end: pricingWindowEnd },
      true,
    );
    pricesCacheRef.current = {
      compute: computeCircuitDaysPrices,
      windowEnd: pricingWindowEnd,
      prices,
    };
    return prices;
  }, [computeCircuitDaysPrices, enabled, pricingWindowEnd]);

  const lastPricedStartDate = useMemo(
    () =>
      enabled ? getLastPricedStartDate(daysTrackingMultiDayBenefits) : null,
    [daysTrackingMultiDayBenefits, enabled],
  );

  const circuitLength = daysTrackingMultiDayBenefits.length;

  // Garantit que toutes les dates de départ jusqu'à `startDate` sont tarifées,
  // c'est-à-dire que la fenêtre couvre aussi le dernier jour du circuit.
  const ensurePricedUntil = useCallback(
    (startDate: Date) => {
      const end = min([
        addDays(startOfDay(startDate), Math.max(circuitLength - 1, 0)),
        getMaxPricingDate(),
      ]);
      setPricingWindowEnd((prevEnd) => (isAfter(end, prevEnd) ? end : prevEnd));
    },
    [circuitLength],
  );

  const circuitDaysPriceByStartDate = useCallback(
    (startDate: Date) =>
      enabled
        ? circuitDaysObject.map((circuitDay, indexDay) =>
            getCircuitDayTotalPrice({
              circuitDayPrices: circuitDay,
              day: addDays(startDate, indexDay),
              targetCurrency,
              convertPrice:
                targetCurrency !== undefined ? convertPrice : undefined,
            }),
          )
        : [],
    [circuitDaysObject, convertPrice, enabled, targetCurrency],
  );

  const findMinimumPriceForDate = useCallback(
    (date: Date) =>
      enabled
        ? circuitDaysPriceByStartDate(date).reduce((a, b) => a + b, 0)
        : 0,
    [circuitDaysPriceByStartDate, enabled],
  );

  return {
    daysTrackingMultiDayBenefits,
    circuitDaysObject,
    circuitDaysPriceByStartDate,
    findMinimumPriceForDate,
    lastPricedStartDate,
    ensurePricedUntil,
  };
};

export const useCircuitStepPricing = ({
  circuitStepPricing,
  date,
  step,
  language,
}: {
  circuitStepPricing: CircuitDayPrices[number];
  date: Date;
  step: CircuitDayFormData['steps'][number];
  language?: SupportedLocale;
}) => {
  const getStandardBenefitNameWithPricing = useCallback(
    (benefitType: keyof typeof step) => {
      const translatedName = language
        ? step[benefitType].standardBenefit?.translations?.[language]?.name
        : step[benefitType].standardBenefit?.name;
      return step[benefitType].standardBenefit
        ? {
            name: translatedName ?? step[benefitType].standardBenefit.name,
            price: circuitStepPricing[benefitType]?.standardBenefit?.prices.get(
              priceDateKey(date),
            ),
            marginRate:
              circuitStepPricing[benefitType]?.standardBenefit?.marginRate,
            currency: step[benefitType].standardBenefit.currency,
          }
        : undefined;
    },
    [circuitStepPricing, date, language, step],
  );

  const standardTransport = useMemo(
    () => getStandardBenefitNameWithPricing('transport'),
    [getStandardBenefitNameWithPricing],
  );

  const standardActivity = useMemo(
    () => getStandardBenefitNameWithPricing('activities'),
    [getStandardBenefitNameWithPricing],
  );

  const standardMeal = useMemo(
    () => getStandardBenefitNameWithPricing('meals'),
    [getStandardBenefitNameWithPricing],
  );

  const standardAccommodation = useMemo(
    () => getStandardBenefitNameWithPricing('accommodations'),
    [getStandardBenefitNameWithPricing],
  );

  const getOptionalBenefitsNamesWithPricing = useCallback(
    (benefitType: keyof typeof step) => {
      return step[benefitType].optionalBenefits
        ? step[benefitType].optionalBenefits.map((benefit, indexBenefit) => {
            const translatedName = language
              ? benefit.translations?.[language]?.name
              : benefit.name;
            const optionalPricing =
              circuitStepPricing[benefitType]?.optionalBenefits?.[indexBenefit];
            const priceForDate = optionalPricing?.prices.get(
              priceDateKey(date),
            );
            return {
              id: benefit.id,
              name: translatedName ?? benefit.name,
              price: priceForDate,
              marginRate:
                circuitStepPricing[benefitType]?.optionalBenefits?.[
                  indexBenefit
                ]?.marginRate,
              currency: benefit.currency,
            };
          })
        : [];
    },
    [circuitStepPricing, date, language, step],
  );

  const optionalTransportBenefits = useMemo(
    () => getOptionalBenefitsNamesWithPricing('transport'),
    [getOptionalBenefitsNamesWithPricing],
  );

  const optionalActivityBenefits = useMemo(
    () => getOptionalBenefitsNamesWithPricing('activities'),
    [getOptionalBenefitsNamesWithPricing],
  );

  const optionalMealsBenefits = useMemo(
    () => getOptionalBenefitsNamesWithPricing('meals'),
    [getOptionalBenefitsNamesWithPricing],
  );

  const optionalAccommodationsBenefits = useMemo(
    () => getOptionalBenefitsNamesWithPricing('accommodations'),
    [getOptionalBenefitsNamesWithPricing],
  );

  const atLeastOneStandard = useMemo(
    () =>
      (standardAccommodation ??
      standardMeal ??
      standardActivity ??
      standardTransport)
        ? true
        : false,
    [standardAccommodation, standardActivity, standardMeal, standardTransport],
  );

  const optionalLength = useMemo(
    () =>
      optionalAccommodationsBenefits?.length +
      optionalMealsBenefits?.length +
      optionalActivityBenefits?.length +
      optionalTransportBenefits?.length,
    [
      optionalAccommodationsBenefits,
      optionalMealsBenefits,
      optionalActivityBenefits,
      optionalTransportBenefits,
    ],
  );

  return {
    standardTransport,
    standardActivity,
    standardMeal,
    standardAccommodation,
    optionalTransportBenefits,
    optionalActivityBenefits,
    optionalMealsBenefits,
    optionalAccommodationsBenefits,
    atLeastOneStandard,
    optionalLength,
  };
};
